/**
 * Household children JSON (households.children; family_profile.children is a mirror).
 * Shape: { id, birth_ym, name?, setting?: 'kita'|'kindergarten', kita_weekdays?, hort_weekdays?, audience?: string[], school_id?: string, class_id?: string }
 * Keep in sync with server/lib/familyChildren.js (server cannot named-import this CRA CJS tree).
 */

const TODDLER_MIN_MONTHS = 12;
const PRESCHOOL_MIN_MONTHS = 36;
const PRESCHOOL_MAX_MONTHS = 84;

export const CHILD_SETTING_KITA = "kita";
export const CHILD_SETTING_KINDERGARTEN = "kindergarten";

export const SETTING_LABELS = {
  kita: "KiTa",
  kindergarten: "Kindergarten",
};

export const SETTING_OPTIONS = [
  { value: CHILD_SETTING_KITA, label: "KiTa" },
  { value: CHILD_SETTING_KINDERGARTEN, label: "Kindergarten" },
];

/** Stadt Zürich Kindergarten default: school week, parents can adjust. */
export const DEFAULT_KINDERGARTEN_WEEKDAYS = [1, 2, 3, 4, 5];

/** @param {string} birthYm @param {Date} [now] */
export function monthsOld(birthYm, now = new Date()) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(birthYm || "").trim());
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;
  const months = (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - month);
  return months >= 0 ? months : null;
}

/** @param {number|null} months @returns {'baby'|'toddler'|'preschool'|'school'|null} */
export function ageBandFromMonths(months) {
  if (months == null) return null;
  if (months < TODDLER_MIN_MONTHS) return "baby";
  if (months < PRESCHOOL_MIN_MONTHS) return "toddler";
  if (months < PRESCHOOL_MAX_MONTHS) return "preschool";
  return "school";
}

export function ageBandFromBirthYm(birthYm, now = new Date()) {
  return ageBandFromMonths(monthsOld(birthYm, now));
}

export function newChildId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** @param {unknown} days @returns {number[]} */
export function normalizeWeekdays(days) {
  return [...new Set((Array.isArray(days) ? days : []).map(Number))].filter(
    (n) => Number.isInteger(n) && n >= 1 && n <= 7,
  ).sort((a, b) => a - b);
}

const AUDIENCE_MAX = 8;
const AUDIENCE_LABEL_MAX = 80;
const COHORT_HINT_RE =
  /gruppe|group|jahr|kindergartenjahr|sezione|classe|klasse|\bkg\b|kiga|hort|nido|infanzia|\banno\b/i;

export function foldAudienceLabel(raw) {
  return String(raw || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

export function normalizeAudienceLabel(raw) {
  const t = String(raw || "").replace(/\s+/g, " ").trim();
  if (t.length < 2 || t.length > AUDIENCE_LABEL_MAX) return null;
  return t;
}

export function normalizeAudienceLabels(raw) {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const n = normalizeAudienceLabel(item);
    if (!n) continue;
    const key = foldAudienceLabel(n);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(n);
    if (out.length >= AUDIENCE_MAX) break;
  }
  return out;
}

/** True when a `child` string looks like a Gruppe/Jahr, not a first name. */
export function isCohortLabel(raw) {
  const t = String(raw || "").trim();
  if (!t) return false;
  if (COHORT_HINT_RE.test(t)) return true;
  if (/\d/.test(t)) return true;
  if (/\s/.test(t) && !normalizeChildName(t)) return true;
  return false;
}

/**
 * Split LLM `child` (legacy: name or Gruppe) from `applies_to`.
 * @param {unknown} child
 * @param {unknown} appliesTo
 * @returns {{ child: string|null, applies_to: string[] }}
 */
export function splitLegacyChildField(child, appliesTo) {
  const labels = normalizeAudienceLabels(appliesTo);
  const raw = String(child || "").trim();
  if (!raw) return { child: null, applies_to: labels };

  const parts = raw
    .split(/\s*(?:,|;|&|\/|\bund\b)\s+/i)
    .map((s) => s.trim())
    .filter(Boolean);
  const names = [];
  const extra = [];
  for (const part of parts) {
    if (isCohortLabel(part)) {
      extra.push(part);
      continue;
    }
    const parsed = normalizeChildName(part);
    if (parsed && !/\s/.test(part)) {
      names.push(parsed);
      continue;
    }
    extra.push(part);
  }
  return {
    child: names.length ? names.join(" & ") : null,
    applies_to: normalizeAudienceLabels([...labels, ...extra]),
  };
}

export function eventAppliesTo(event) {
  return splitLegacyChildField(event?.child, event?.applies_to).applies_to;
}

export function audienceLabelsOverlap(a, b) {
  const known = new Set(normalizeAudienceLabels(a).map(foldAudienceLabel));
  return normalizeAudienceLabels(b).some((label) => known.has(foldAudienceLabel(label)));
}

/**
 * Collect Gruppe/Jahr labels from flattened items and mailbox rows.
 * @param {object[]|null|undefined} items
 */
export function collectAppliesToLabels(items) {
  const all = [];
  for (const item of items || []) {
    all.push(...eventAppliesTo(item));
    for (const ev of item.events || []) {
      all.push(...eventAppliesTo(ev));
    }
  }
  return normalizeAudienceLabels(all);
}

/** Institution-wide or hallucinated labels — not a Gruppe/Jahr chip. */
const GENERIC_AUDIENCE_FOLDED = new Set([
  "kita",
  "kindergarten",
  "kiga",
  "kg",
  "hort",
  "mittagstisch",
  "tageshort",
  "nachmittagshort",
  "kinder",
  "kind",
  "eltern",
  "alle",
  "allekinder",
  "altegruppen",
  "allegruppen",
  "allejahrgaenge",
  "familie",
  "schule",
  "institution",
  "gruppe",
  "gruppen",
  "group",
  "groups",
  "jahrgang",
  "jahrgaenge",
  "jahrgange",
  "klasse",
  "klassen",
  "classe",
  "sezione",
  "nido",
  "infanzia",
]);

/**
 * True when a collected `applies_to` value is worth offering as a tap chip.
 * Named groups (Sonnengruppe, Füchse) stay; KiTa/Kindergarten/dates/child names do not.
 * @param {unknown} raw
 * @param {unknown} [childNames]
 */
export function isAudienceAskChip(raw, childNames = []) {
  const n = normalizeAudienceLabel(raw);
  if (!n) return false;
  const folded = foldAudienceLabel(n);
  if (!folded || GENERIC_AUDIENCE_FOLDED.has(folded)) return false;
  if (/^\d+$/.test(folded)) return false;
  const nameSet = new Set(
    (Array.isArray(childNames) ? childNames : [])
      .map((name) => foldAudienceLabel(name))
      .filter(Boolean),
  );
  if (nameSet.has(folded)) return false;
  return true;
}

/**
 * Gruppe/Jahr chips to show in chat. Empty means do not ask.
 * Filters before the label cap so KiTa/Kindergarten noise cannot hide real groups.
 * @param {unknown} raw
 * @param {unknown} [childNames]
 */
export function audienceAskChipLabels(raw, childNames = []) {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    if (!isAudienceAskChip(item, childNames)) continue;
    const n = normalizeAudienceLabel(item);
    const key = foldAudienceLabel(n);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(n);
    if (out.length >= AUDIENCE_MAX) break;
  }
  return out;
}

/**
 * Collect tap-able Gruppe/Jahr chips from flattened items and mailbox rows.
 * @param {object[]|null|undefined} items
 * @param {unknown} [childNames]
 */
export function collectAudienceAskLabels(items, childNames = []) {
  const all = [];
  for (const item of items || []) {
    all.push(...eventAppliesTo(item));
    for (const ev of item.events || []) {
      all.push(...eventAppliesTo(ev));
    }
  }
  return audienceAskChipLabels(all, childNames);
}

export function normalizeChildName(raw) {
  const t = String(raw || "").trim().replace(/\s+/g, " ");
  if (t.length < 2 || t.length > 40) return null;
  if (!/^[A-Za-zÀ-ÿÄÖÜäöüß'’.\- ]+$/.test(t)) return null;
  return t;
}

/**
 * @param {string} text
 * @param {number} count
 * @returns {string[]}
 */
export function parseChildNames(text, count) {
  const max = Number.isInteger(count) && count > 0 ? count : 8;
  const trimmed = String(text || "").trim();
  if (!trimmed) return [];
  let tokens = trimmed.split(/\s*(?:,|;|&|\/|\bund\b)\s+/i).map((s) => s.trim()).filter(Boolean);
  if (tokens.length === 1 && max > 1) {
    tokens = trimmed.split(/\s+/).map((s) => s.trim()).filter(Boolean);
  }
  const names = [];
  for (const tok of tokens) {
    const n = normalizeChildName(tok);
    if (n) names.push(n);
    if (names.length >= max) break;
  }
  return names;
}

function cleanPackId(value) {
  const id = String(value || "").trim().toLowerCase();
  return /^[a-z0-9][a-z0-9-]{0,79}$/.test(id) ? id : null;
}

/**
 * @param {unknown} raw
 * @returns {{ id: string, birth_ym: string|null, name: string|null, setting: 'kita'|'kindergarten'|null, kita_weekdays?: number[], hort_weekdays?: number[], audience?: string[], school_id?: string, class_id?: string } | null}
 */
export function normalizeChild(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const birthYm = String(raw.birth_ym || "").trim();
  const birth_ym = /^\d{4}-\d{2}$/.test(birthYm) ? birthYm : null;
  const setting =
    raw.setting === CHILD_SETTING_KITA || raw.setting === CHILD_SETTING_KINDERGARTEN
      ? raw.setting
      : null;
  const kita_weekdays = normalizeWeekdays(raw.kita_weekdays);
  const hortSpecified = Array.isArray(raw.hort_weekdays);
  const hort_weekdays = hortSpecified ? normalizeWeekdays(raw.hort_weekdays) : undefined;
  const audienceSpecified = Array.isArray(raw.audience);
  const audience = audienceSpecified ? normalizeAudienceLabels(raw.audience) : undefined;
  const school_id = cleanPackId(raw.school_id);
  const class_id = cleanPackId(raw.class_id);
  const id = String(raw.id || "").trim() || newChildId();
  return {
    id,
    birth_ym,
    name: normalizeChildName(raw.name),
    setting,
    ...(kita_weekdays.length ? { kita_weekdays } : {}),
    ...(hortSpecified ? { hort_weekdays } : {}),
    ...(audienceSpecified ? { audience } : {}),
    ...(school_id ? { school_id } : {}),
    ...(class_id ? { class_id } : {}),
  };
}

/** @param {unknown} list */
export function normalizeChildren(list) {
  if (!Array.isArray(list)) return [];
  return list.map((row) => normalizeChild(row)).filter(Boolean);
}

/**
 * Prefer the household list when it has entries; keep the profile list if the
 * household row is still an empty array (failed/partial sync).
 * @param {unknown} shared
 * @param {unknown} fallback
 */
export function overlaySharedList(shared, fallback) {
  const fallbackArr = Array.isArray(fallback) ? fallback : [];
  if (!Array.isArray(shared)) return fallbackArr;
  if (shared.length > 0) return shared;
  if (fallbackArr.length > 0) return fallbackArr;
  return shared;
}

export function isChildrenProfile(value) {
  return (
    Array.isArray(value) &&
    value.some((item) => item && typeof item === "object" && !Array.isArray(item))
  );
}

/** @param {{ setting?: string|null, birth_ym?: string|null } | null | undefined} child */
export function inferSetting(child, now = new Date()) {
  if (child?.setting === CHILD_SETTING_KITA || child?.setting === CHILD_SETTING_KINDERGARTEN) {
    return child.setting;
  }
  const band = ageBandFromBirthYm(child?.birth_ym, now);
  if (band === "baby" || band === "toddler") return CHILD_SETTING_KITA;
  return null;
}

export function isKitaChild(child, now = new Date()) {
  return inferSetting(child, now) === CHILD_SETTING_KITA;
}

export function isKindergartenChild(child, now = new Date()) {
  return inferSetting(child, now) === CHILD_SETTING_KINDERGARTEN;
}

/** 3–6 with no KiTa/Kindergarten answer. Über 6 is not asked. */
export function needsSettingAsk(child, now = new Date()) {
  if (child?.setting === CHILD_SETTING_KITA || child?.setting === CHILD_SETTING_KINDERGARTEN) {
    return false;
  }
  if (inferSetting(child, now)) return false;
  return ageBandFromBirthYm(child?.birth_ym, now) === "preschool";
}

/** Two or more children, and fewer than two names to tell them apart. */
export function childrenNeedNamesToSort(children) {
  const list = normalizeChildren(children);
  if (list.length < 2) return false;
  return list.filter((child) => child.name).length < 2;
}

export function childrenNeedingKitaDays(children, now = new Date()) {
  return normalizeChildren(children).filter(
    (child) => isKitaChild(child, now) && !(child.kita_weekdays && child.kita_weekdays.length),
  );
}

/** Kindergarten children who have not been asked about Hort yet (`hort_weekdays` missing). */
export function childrenNeedingHortAsk(children, now = new Date()) {
  return normalizeChildren(children).filter(
    (child) => isKindergartenChild(child, now) && !Array.isArray(child.hort_weekdays),
  );
}

/**
 * Children who still need a Gruppe/Jahr confirm, or have new unseen labels.
 * Empty `audience: []` means the parent skipped — do not ask again.
 * Generic institution labels are not chips and do not trigger the ask.
 * @param {unknown} children
 * @param {unknown} appliesToLabels
 */
export function childrenNeedingAudienceAsk(children, appliesToLabels) {
  const list = normalizeChildren(children);
  const childNames = list.map((c) => c.name).filter(Boolean);
  const labels = audienceAskChipLabels(appliesToLabels, childNames);
  if (labels.length === 0) return [];
  const incoming = new Set(labels.map(foldAudienceLabel));
  return list.filter((child) => {
    if (!Array.isArray(child.audience)) return true;
    if (child.audience.length === 0) return false;
    const known = new Set(child.audience.map(foldAudienceLabel));
    return [...incoming].some((key) => !known.has(key));
  });
}

export function withChildSetting(children, childId, setting, now = new Date()) {
  const nextSetting =
    setting === CHILD_SETTING_KITA || setting === CHILD_SETTING_KINDERGARTEN
      ? setting
      : null;
  return normalizeChildren(children).map((child) => {
    if (childId && child.id !== childId) return child;
    const next = { ...child, setting: nextSetting };
    if (
      nextSetting === CHILD_SETTING_KINDERGARTEN &&
      !(next.kita_weekdays && next.kita_weekdays.length)
    ) {
      next.kita_weekdays = [...DEFAULT_KINDERGARTEN_WEEKDAYS];
    }
    return next;
  });
}

export function childDisplayName(child, index = 0) {
  if (child?.name) return child.name;
  return `Kind ${index + 1}`;
}

function foldName(raw) {
  return String(raw || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .trim();
}

/**
 * @param {unknown} eventChild
 * @param {unknown} children
 */
export function matchProfileChild(eventChild, children) {
  const needle = foldName(eventChild);
  if (!needle) return null;
  const list = normalizeChildren(children);
  const exact = list.filter((c) => c.name && foldName(c.name) === needle);
  if (exact.length === 1) return exact[0];
  const partial = list.filter((c) => {
    const n = foldName(c.name);
    return n && (n.startsWith(needle) || needle.startsWith(n));
  });
  if (partial.length === 1) return partial[0];
  return null;
}

/**
 * Profile children named in a label. "Emma & Luca" matches both; a single
 * first name matches one. Tokens are matched on their own so the joined
 * string does not collapse to the first name.
 * @param {unknown} label
 * @param {unknown} children
 */
export function childrenNamedInLabel(label, children) {
  const list = normalizeChildren(children).filter((c) => c.name);
  const raw = String(label || "").trim();
  if (!raw || list.length === 0) return [];
  const parts = raw
    .split(/\s*(?:,|;|&|\/|\bund\b)\s+/i)
    .map((s) => s.trim())
    .filter(Boolean);
  const tokens = parts.length > 1 ? parts : [raw];
  const found = [];
  for (const token of tokens) {
    const hit = matchProfileChild(token, list);
    if (hit && !found.some((c) => c.id === hit.id)) found.push(hit);
  }
  return found;
}

export function isHortShapedEvent(event) {
  const blob = `${event?.title || ""} ${event?.source_quote || ""}`.toLowerCase();
  return /\bhort\b|mittagstisch|nachmittagshort|tageshort/.test(blob);
}

function kindergartenWeekdays(child) {
  if (child?.kita_weekdays && child.kita_weekdays.length) return child.kita_weekdays;
  return [...DEFAULT_KINDERGARTEN_WEEKDAYS];
}

/** Same non-empty kita_weekdays on every child — safe to apply without a name match. */
function sharedStoredKitaWeekdays(list) {
  const stored = (list || []).map((child) => child.kita_weekdays || []);
  if (stored.length === 0 || stored.some((days) => days.length === 0)) return [];
  const first = JSON.stringify(stored[0]);
  if (!stored.every((days) => JSON.stringify(days) === first)) return [];
  return stored[0];
}

/**
 * Weekdays that make a care-day Termin "ours" for this household.
 * Unmatched mixed households return [] (unknown → keep visible).
 * @param {object|null|undefined} event
 * @param {unknown} children
 * @returns {number[]}
 */
export function attendanceWeekdaysForEvent(event, children) {
  const list = normalizeChildren(children);
  if (list.length === 0) return [];
  const hort = isHortShapedEvent(event);
  const split = splitLegacyChildField(event?.child, event?.applies_to);
  const namedHits = childrenNamedInLabel(split.child || event?.child, list);

  if (namedHits.length > 1) {
    const set = new Set();
    for (const child of namedHits) {
      const days = hort
        ? child.hort_weekdays || []
        : isKindergartenChild(child)
          ? kindergartenWeekdays(child)
          : child.kita_weekdays || [];
      for (const day of days) set.add(day);
    }
    return [...set].sort((a, b) => a - b);
  }

  const matched = namedHits[0] || null;

  if (hort) {
    const hortKids = list.filter((c) => Array.isArray(c.hort_weekdays) && c.hort_weekdays.length);
    const target = matched || (hortKids.length === 1 ? hortKids[0] : null);
    if (!target) return [];
    return target.hort_weekdays || [];
  }

  if (matched) {
    if (isKindergartenChild(matched)) return kindergartenWeekdays(matched);
    return matched.kita_weekdays || [];
  }

  if (list.length === 1) {
    if (isKindergartenChild(list[0])) return kindergartenWeekdays(list[0]);
    return list[0].kita_weekdays || [];
  }

  return sharedStoredKitaWeekdays(list);
}

/**
 * @param {unknown} children
 * @param {string} childId
 * @param {'kita'|'hort'} kind
 * @param {unknown} days
 */
export function withWeekdaysOnChild(children, childId, kind, days) {
  const normalized = normalizeWeekdays(days);
  const list = normalizeChildren(children);
  const fallback = list.length > 0 ? list : [normalizeChild({ birth_ym: null })];
  return fallback.map((child) => {
    if (childId && child.id !== childId) return child;
    if (kind === "hort") {
      return { ...child, hort_weekdays: normalized };
    }
    return { ...child, kita_weekdays: normalized };
  });
}

/**
 * @param {unknown} children
 * @param {string} childId
 * @param {unknown} labels
 */
export function withAudienceOnChild(children, childId, labels) {
  const audience = normalizeAudienceLabels(labels);
  const list = normalizeChildren(children);
  const fallback = list.length > 0 ? list : [normalizeChild({ birth_ym: null })];
  return fallback.map((child) => {
    if (childId && child.id !== childId) return child;
    return { ...child, audience };
  });
}

/**
 * Keep labels not in this chip set; replace overlapping ones with `selected`.
 * @param {unknown} existing
 * @param {unknown} selected
 * @param {unknown} chipLabels
 */
export function mergeAudienceKeepingUnseen(existing, selected, chipLabels) {
  const chips = new Set(normalizeAudienceLabels(chipLabels).map(foldAudienceLabel));
  const keep = normalizeAudienceLabels(existing).filter(
    (label) => !chips.has(foldAudienceLabel(label)),
  );
  return normalizeAudienceLabels([...keep, ...normalizeAudienceLabels(selected)]);
}

/**
 * Score a kids list so join can pick the more complete one (days beat extra unnamed kids).
 * @param {unknown} list
 */
export function childrenRichness(list) {
  return normalizeChildren(list).reduce((score, child) => {
    let s = 1;
    if (child.birth_ym) s += 1;
    if (child.name) s += 2;
    if (child.setting) s += 1;
    if (child.kita_weekdays?.length) s += 4;
    if (Array.isArray(child.hort_weekdays)) s += 1;
    if (child.audience?.length) s += 2;
    if (child.school_id) s += 1;
    return score + s;
  }, 0);
}

/**
 * Prefer the richer kids list; on a tie keep `preferred` (inviter).
 * Does not concatenate — that would duplicate the same child.
 * @param {unknown} preferred
 * @param {unknown} other
 */
export function pickRicherChildren(preferred, other) {
  const a = normalizeChildren(preferred);
  const b = normalizeChildren(other);
  return childrenRichness(b) > childrenRichness(a) ? b : a;
}

/**
 * Dated attention items that are not clearly for one named child.
 * @param {object[]} items
 * @param {unknown} children
 */
export function unmatchedChildAttentionItems(items, children) {
  const named = normalizeChildren(children).filter((c) => c.name);
  if (named.length < 2) return [];
  return (items || []).filter((item) => {
    const action = String(item?.action_type || item?.actionType || "").toLowerCase();
    if (action === "info") return false;
    if (!item?.date && !item?.title) return false;
    return childrenNamedInLabel(item?.child, named).length === 0;
  });
}

export function mailboxHasHortEvents(rows) {
  for (const row of rows || []) {
    for (const event of row.events || []) {
      if (isHortShapedEvent(event)) return true;
    }
  }
  return false;
}
